class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'shizennoshirushi-recently-viewed';
    this.maximumStoredProducts = 12;
  }

  connectedCallback() {
    this.currentHandle = this.dataset.currentProduct || '';
    this.limit = Number.parseInt(this.dataset.limit, 10) || 4;
    this.moneyFormat = this.dataset.moneyFormat || '¥{{amount_no_decimals}}';
    this.list = this.querySelector('[data-recently-viewed-list]');

    const handles = this.readHandles();
    const productsToDisplay = handles
      .filter((handle) => handle !== this.currentHandle)
      .slice(0, this.limit);

    if (this.currentHandle) {
      this.saveHandles([
        this.currentHandle,
        ...handles.filter((handle) => handle !== this.currentHandle),
      ]);
    }

    if (productsToDisplay.length > 0) {
      this.renderProducts(productsToDisplay);
    }
  }

  readHandles() {
    try {
      const storedValue = JSON.parse(localStorage.getItem(this.storageKey));
      return Array.isArray(storedValue)
        ? storedValue.filter((handle) => typeof handle === 'string' && handle.length > 0)
        : [];
    } catch (error) {
      return [];
    }
  }

  saveHandles(handles) {
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify(handles.slice(0, this.maximumStoredProducts))
      );
    } catch (error) {
      // Browsing history is an enhancement; the storefront remains usable if storage is unavailable.
    }
  }

  async renderProducts(handles) {
    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
          return response.ok ? response.json() : null;
        } catch (error) {
          return null;
        }
      })
    );

    const availableProducts = products.filter(Boolean);

    if (availableProducts.length === 0) return;

    this.list.innerHTML = availableProducts.map((product) => this.productCard(product)).join('');
    this.classList.add('is-visible');
  }

  productCard(product) {
    const productUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(product.handle)}`;
    const image = product.featured_image || product.images?.[0] || '';
    const imageMarkup = image
      ? `<img
          class="recently-viewed-products__image"
          src="${this.escapeAttribute(this.resizeImage(image, 600))}"
          alt="${this.escapeAttribute(product.title)}"
          width="600"
          height="600"
          loading="lazy"
        >`
      : '';
    const compareAtPrice =
      product.compare_at_price > product.price
        ? `<span class="recently-viewed-products__compare-price">${this.formatMoney(product.compare_at_price)}</span>`
        : '';

    return `
      <li class="recently-viewed-products__item">
        <a class="recently-viewed-products__link" href="${this.escapeAttribute(productUrl)}">
          <div class="recently-viewed-products__image-wrapper">${imageMarkup}</div>
          <h3 class="recently-viewed-products__title">${this.escapeHtml(product.title)}</h3>
          <p class="recently-viewed-products__price">
            ${this.formatMoney(product.price)}
            ${compareAtPrice}
          </p>
        </a>
      </li>
    `;
  }

  resizeImage(url, width) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  formatMoney(cents) {
    const amount = Number(cents) / 100;
    const amountWithDecimals = amount.toLocaleString('ja-JP', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const amountWithoutDecimals = Math.round(amount).toLocaleString('ja-JP');

    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountWithoutDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amountWithDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithDecimals.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountWithoutDecimals);
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(String(value)).replace(/"/g, '&quot;');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
